import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@apollo/client/react';
import { generateClient } from 'aws-amplify/api';
import { GET_MY_MESSAGES } from '../graphql/clientQueries';
import { useClientAuth } from './useClientAuth';
import type { Message } from '../types';

interface UseUnreadMessagesReturn {
  /** Total unread message count across all conversations */
  unreadCount: number;
  /** Decrement the unread count (call when messages are read) */
  decrementUnreadCount: () => void;
  /** Refetch messages to get accurate unread count */
  refetch: () => void;
}

interface ListMyMessagesResponse {
  listMyMessages: {
    items: Message[];
    nextToken?: string;
  };
}

/**
 * useUnreadMessages - Unread message count hook
 * 
 * Fetches the total unread message count from all messages and subscribes
 * to real-time new message notifications to keep the count updated.
 * 
 * The unread count is displayed as a badge on the Chat tab in the navigation.
 * 
 * Validates: Requirements 2.6
 */
export function useUnreadMessages(): UseUnreadMessagesReturn {
  const { user } = useClientAuth();
  const [localUnreadCount, setLocalUnreadCount] = useState<number | null>(null);

  // Fetch messages to calculate total unread count
  const {
    data: messagesData,
    refetch: refetchMessages,
  } = useQuery<ListMyMessagesResponse>(GET_MY_MESSAGES, {
    variables: { limit: 100 },
    fetchPolicy: 'cache-and-network',
  });

  // Subscribe to new message notifications using Amplify (like staff side)
  useEffect(() => {
    if (!user?.id) return;

    const client = generateClient();
    
    const sub = client.graphql({
      query: `
        subscription OnMessageReceived($recipientId: ID!) {
          onMessageReceived(recipientId: $recipientId) {
            id
            conversationId
            clientId
            staffId
            senderId
            recipientId
            content
            isRead
            isEdited
            createdAt
          }
        }
      `,
      variables: { recipientId: user.id },
    });

    const subscription = (sub as any).subscribe({
      next: ({ data }: any) => {
        if (data?.onMessageReceived) {
          // Increment unread count when a new message is received
          setLocalUnreadCount((prev) => (prev ?? 0) + 1);
        }
      },
      error: (error: any) => {
        console.error('New message subscription error:', error);
      },
    });

    return () => subscription.unsubscribe();
  }, [user?.id]);

  // Calculate total unread count from messages where user is recipient and not read
  const serverUnreadCount = messagesData?.listMyMessages?.items?.filter(
    (msg) => !msg.isRead && msg.recipientId === user?.id
  ).length ?? 0;

  // Use local count if set (from subscription updates), otherwise use server count
  const unreadCount = localUnreadCount ?? serverUnreadCount;

  // Sync local count with server count when server data changes
  useEffect(() => {
    if (messagesData?.listMyMessages?.items) {
      setLocalUnreadCount(serverUnreadCount);
    }
  }, [messagesData, serverUnreadCount]);

  // Decrement unread count when messages are read
  const decrementUnreadCount = useCallback(() => {
    setLocalUnreadCount((prev) => Math.max(0, (prev ?? 0) - 1));
  }, []);

  // Refetch messages to get accurate unread count from server
  const refetch = useCallback(() => {
    refetchMessages();
    // Reset local count to sync with server on next data fetch
    setLocalUnreadCount(null);
  }, [refetchMessages]);

  return {
    unreadCount,
    decrementUnreadCount,
    refetch,
  };
}
